/** @format */

import { Switch } from "@headlessui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCalendarFeed } from "@/hooks/useCalendarFeed";

const RUN_TYPE_OPTIONS: { value: number; label: string; helper: string }[] = [
  { value: 0, label: "Non-track events", helper: "Media days, garage hours, meetings" },
  { value: 1, label: "Practice", helper: "" },
  { value: 2, label: "Qualifying", helper: "" },
  { value: 3, label: "Race", helper: "" },
];

const SERIES_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Cup" },
  { value: 2, label: "Xfinity" },
  { value: 3, label: "Craftsman Truck" },
];

function toggle(set: number[], value: number): number[] {
  return set.includes(value) ? set.filter(v => v !== value) : [...set, value];
}

export function CalendarFeedSettings() {
  const { feed, isLoading, updateFeed, isUpdating, rotateToken, isRotating } =
    useCalendarFeed();

  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current !== null) {
        clearTimeout(copiedTimeoutRef.current);
      }
    },
    []
  );

  const copyUrl = useCallback(async () => {
    if (!feed?.subscribeUrl) return;
    await navigator.clipboard.writeText(feed.subscribeUrl);
    setCopied(true);
    if (copiedTimeoutRef.current !== null) {
      clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = setTimeout(() => {
      copiedTimeoutRef.current = null;
      setCopied(false);
    }, 2000);
  }, [feed?.subscribeUrl]);

  if (isLoading || !feed) {
    return <div className="p-4 text-slate-500 text-sm">Loading calendar feed…</div>;
  }

  const selectedRunTypes = feed.runTypes.length > 0 ? feed.runTypes : [2, 3];
  const selectedSeries = feed.seriesNids.length > 0 ? feed.seriesNids : [1, 2, 3];

  return (
    <section className="border border-slate-700 rounded-lg p-5 bg-slate-900/60">
      <header className="mb-4">
        <h3 className="text-lg font-semibold text-white">Calendar feed</h3>
        <p className="text-sm text-slate-400 mt-1">
          Subscribe to the NASCAR schedule from Apple Calendar, Google Calendar, or
          Outlook. The URL updates automatically every 15 minutes.
        </p>
      </header>

      <div className="flex items-center justify-between gap-4 py-2 border-b border-slate-800">
        <div>
          <p className="text-sm text-slate-200">Enable feed</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Generate a personal subscription URL
          </p>
        </div>
        <Switch
          checked={feed.enabled}
          onChange={enabled => {
            // Seed sensible defaults on first enable
            const updates: Partial<typeof feed> = { enabled };
            if (enabled && feed.runTypes.length === 0) {
              updates.runTypes = [2, 3];
            }
            if (enabled && feed.seriesNids.length === 0) {
              updates.seriesNids = [1, 2, 3];
            }
            updateFeed(updates);
          }}
          className={`${
            feed.enabled ? "bg-green-500" : "bg-slate-600"
          } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200`}
        >
          <span
            className={`${
              feed.enabled ? "translate-x-5" : "translate-x-0"
            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200`}
          />
        </Switch>
      </div>

      {feed.enabled && (
        <>
          <fieldset className="mt-5">
            <legend className="text-sm font-medium text-slate-300 mb-2">
              Session types
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {RUN_TYPE_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-start gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedRunTypes.includes(opt.value)}
                    onChange={() =>
                      updateFeed({
                        runTypes: toggle(selectedRunTypes, opt.value),
                      })
                    }
                    className="mt-1 accent-cPurple-500"
                  />
                  <span className="min-w-0">
                    <span className="text-sm text-slate-200">{opt.label}</span>
                    {opt.helper && (
                      <span className="block text-xs text-slate-500">{opt.helper}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="mt-5">
            <legend className="text-sm font-medium text-slate-300 mb-2">Series</legend>
            <div className="flex flex-wrap gap-4">
              {SERIES_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedSeries.includes(opt.value)}
                    onChange={() =>
                      updateFeed({
                        seriesNids: toggle(selectedSeries, opt.value),
                      })
                    }
                    className="accent-cPurple-500"
                  />
                  <span className="text-sm text-slate-200">{opt.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="mt-5">
            <label
              className="text-sm font-medium text-slate-300 block mb-2"
              htmlFor="calendar-feed-url"
            >
              Subscribe URL
            </label>
            <div className="flex gap-2">
              <input
                id="calendar-feed-url"
                type="text"
                readOnly
                value={feed.subscribeUrl ?? ""}
                className="flex-1 font-mono text-xs bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-slate-300"
                onFocus={e => e.currentTarget.select()}
              />
              <button
                type="button"
                onClick={copyUrl}
                disabled={!feed.subscribeUrl}
                className="px-3 py-1.5 text-xs font-medium rounded bg-cPurple-600 hover:bg-cPurple-700 text-white disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Paste this into your calendar app's "Add subscription" flow.
            </p>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-800">
            {!confirmRotate ? (
              <button
                type="button"
                onClick={() => setConfirmRotate(true)}
                className="text-xs text-slate-400 hover:text-slate-200 underline"
              >
                Rotate token
              </button>
            ) : (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-amber-400">
                  Rotating invalidates existing calendar subscribers.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    rotateToken();
                    setConfirmRotate(false);
                  }}
                  disabled={isRotating}
                  className="px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white"
                >
                  {isRotating ? "Rotating…" : "Confirm"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRotate(false)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {isUpdating && <p className="text-xs text-slate-500 mt-2">Saving…</p>}
    </section>
  );
}
